// src/utils/sanitiseHtml.ts

/**
 * Strips a news item's HTML down to what the editor can legitimately produce.
 *
 * WHY THIS EXISTS. News content is written as HTML in the admin panel and rendered on the
 * PUBLIC landing page with `dangerouslySetInnerHTML`, to every visitor, logged in or not.
 * Only an admin can write it, so this is not an open hole today — but it is one compromised
 * or careless admin account away from being stored XSS on the front page of the game, and
 * the cost of closing it is this file.
 *
 * ALLOWLIST, NOT BLOCKLIST. Blocklists lose: there is always another way to smuggle a script
 * in. Everything here starts forbidden. The permitted set is exactly what RichTextEditor's
 * toolbar emits — bold, italic, underline, the two list kinds, and four text colours — plus
 * the block and line-break elements contenteditable inserts on its own.
 *
 * Anything not on the list is UNWRAPPED rather than deleted: a `<script>` loses its tags and
 * its text is kept as text, and a stray `<div>` keeps the words inside it. Deleting the
 * subtree would silently eat an operator's paragraph over one bad tag.
 *
 * Runs in the browser only — it uses DOMParser, which is where the content is authored.
 */

const ALLOWED_TAGS = new Set([
    'B', 'STRONG', 'I', 'EM', 'U',
    'UL', 'OL', 'LI',
    'P', 'BR', 'DIV', 'SPAN', 'FONT',
]);

/** Only colour survives, and only as a literal colour. */
const ALLOWED_ATTRS = new Set(['color']);

/** #rgb, #rrggbb, rgb(...) or a bare CSS colour keyword. Anything with a url() or a
 *  semicolon-separated second declaration is refused outright. */
const COLOUR = /^(#[0-9a-f]{3,8}|rgba?\([\d\s.,%]+\)|[a-z]{3,20})$/i;

const safeColour = (value: string): string | null => {
    const trimmed = value.trim().replace(/;$/, '');
    return COLOUR.test(trimmed) ? trimmed : null;
};

/** Replaces a node with its own children, keeping the text. */
const unwrap = (node: Element): void => {
    const parent = node.parentNode;
    if (!parent) return;
    while (node.firstChild) parent.insertBefore(node.firstChild, node);
    parent.removeChild(node);
};

const clean = (root: ParentNode): void => {
    // Snapshot first: unwrapping mutates the tree while we walk it.
    for (const el of Array.from(root.querySelectorAll('*'))) {
        if (!ALLOWED_TAGS.has(el.tagName)) {
            // A script or style carries its payload as TEXT, so unwrapping would paste the
            // payload into the document as visible content. Those two are removed whole.
            if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE') el.remove();
            else unwrap(el);
            continue;
        }

        for (const attr of Array.from(el.attributes)) {
            const name = attr.name.toLowerCase();

            if (name === 'style') {
                // Keep a colour declaration and nothing else; execCommand('foreColor')
                // writes this on a span in most browsers.
                const match = /(?:^|;)\s*color\s*:\s*([^;]+)/i.exec(attr.value);
                const colour = match ? safeColour(match[1]) : null;
                if (colour) el.setAttribute('style', `color: ${colour}`);
                else el.removeAttribute('style');
                continue;
            }

            if (!ALLOWED_ATTRS.has(name)) {
                el.removeAttribute(attr.name);
                continue;
            }

            // `color` on a <font>, the other thing foreColor produces.
            const colour = safeColour(attr.value);
            if (colour) el.setAttribute(name, colour);
            else el.removeAttribute(attr.name);
        }
    }
};

export const sanitiseHtml = (html: string): string => {
    if (!html) return '';
    // A detached document: parsing here never runs scripts and never loads anything.
    const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
    clean(doc.body);
    return doc.body.innerHTML;
};

/** Plain text, for excerpts, search and word counts. */
export const htmlToText = (html: string): string =>
    html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
